import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { candidateService } from '../../services';
import { useAuth } from '../../context/AuthContext';
import { formatCurrency, formatDate } from '../../utils/formatters';
import PartyDonationModal from '../../components/common/PartyDonationModal';
import QRCode from 'react-qr-code';

// Production base URL for QR code
const PRODUCTION_BASE_URL = 'https://fundraiser.rspnepal.org';

const CandidateProfile = () => {
  const { linkOrId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [showDonateModal, setShowDonateModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const qrRef = useRef(null);

  useEffect(() => {
    setImageError(false); // Reset image error when candidate changes
    fetchCandidate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkOrId]);

  // Check for QR code parameter and auto-open donation modal
  useEffect(() => {
    const isQrScan = searchParams.get('is-qr');
    if (isQrScan === 'true' && candidate) {
      setShowDonateModal(true);
      // Remove the query parameter from URL after opening modal
      searchParams.delete('is-qr');
      setSearchParams(searchParams, { replace: true });
    }
  }, [candidate, searchParams, setSearchParams]);

  const fetchCandidate = async () => {
    try {
      const response = await candidateService.getCandidateProfile(linkOrId);
      // Handle API response structure: { status, statusCode, data: { candidate details } }
      const candidateData = response?.data || response;
      setCandidate(candidateData);

      // Redirect to pretty link if available and current URL uses ID
      if (candidateData?.link && linkOrId !== candidateData.link) {
        navigate(`/ candidates / ${candidateData.link} `, { replace: true });
      }
    } catch (error) {
      console.error('Error fetching candidate:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDonateClick = () => {
    setShowDonateModal(true);
  };

  const shareUrl = `${PRODUCTION_BASE_URL} /candidates/${candidate?.link || candidate?.id} `;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleDownloadQR = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      const scale = 3; // High resolution multiplier
      const padding = 60 * scale;
      const qrSize = 300 * scale;
      const headerHeight = 100 * scale;
      const footerHeight = 120 * scale;
      const totalWidth = qrSize + (padding * 2);
      const totalHeight = headerHeight + qrSize + footerHeight + (padding * 2);

      canvas.width = totalWidth;
      canvas.height = totalHeight;

      // White background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Blue border
      ctx.strokeStyle = '#2563eb';
      ctx.lineWidth = 8 * scale;
      ctx.strokeRect(4 * scale, 4 * scale, canvas.width - 8 * scale, canvas.height - 8 * scale);

      // Header text - "Support"
      ctx.fillStyle = '#2563eb';
      ctx.font = `bold ${36 * scale}px Arial`;
      ctx.textAlign = 'center';
      ctx.fillText('🙏 Support', totalWidth / 2, padding + 40 * scale);

      // Candidate name
      ctx.fillStyle = '#1f2937';
      ctx.font = `bold ${32 * scale}px Arial`;
      const candidateName = candidate?.name || 'Candidate';
      ctx.fillText(candidateName, totalWidth / 2, padding + 85 * scale);

      // Draw QR code
      ctx.drawImage(img, padding, headerHeight + padding, qrSize, qrSize);

      // Footer - Constituency info
      ctx.fillStyle = '#6b7280';
      ctx.font = `${24 * scale}px Arial`;
      const location = `${candidate?.constituency || ''}, ${candidate?.district || ''} `;
      ctx.fillText(location, totalWidth / 2, headerHeight + padding + qrSize + 40 * scale);

      // Scan to donate text
      ctx.fillStyle = '#2563eb';
      ctx.font = `bold ${26 * scale}px Arial`;
      ctx.fillText('Scan to Donate', totalWidth / 2, headerHeight + padding + qrSize + 80 * scale);

      // RSP branding
      ctx.fillStyle = '#9ca3af';
      ctx.font = `${20 * scale}px Arial`;
      ctx.fillText('RSP Fundraiser', totalWidth / 2, headerHeight + padding + qrSize + 115 * scale);

      const link = document.createElement('a');
      link.download = `Support_${candidate?.name?.replace(/\s+/g, '_')} _QR.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  const quickAmounts = [100, 500, 1000, 5000, 10000];

  const DetailItem = ({ label, value, icon }) => {
    if (!value || (Array.isArray(value) && value.length === 0)) return null;
    return (
      <div className="flex items-start gap-3 p-4 bg-white rounded-xl border border-gray-100 shadow-sm border-l-4 border-l-primary-500">
        {icon && <div className="mt-1 text-primary-600">{icon}</div>}
        <div>
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{label}</p>
          <div className="text-gray-900 font-medium whitespace-pre-wrap">
            {Array.isArray(value) ? (
              <ul className="list-disc list-inside space-y-1">
                {value.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            ) : value}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!candidate) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Candidate not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="h-80 bg-gradient-to-br from-primary-200 to-primary-400 flex items-center justify-center overflow-hidden relative">
            {candidate.photoKeyUrl && !imageError ? (
              <img
                src={candidate.photoKeyUrl}
                alt={candidate.name}
                className="w-full h-full object-cover"
                onError={() => {
                  setImageError(true);
                }}
              />
            ) : (
              <span className="text-8xl font-bold text-white">{candidate.name.charAt(0)}</span>
            )}
          </div>

          <div className="p-8 md:p-10">
            {/* QR Code Section - Mobile (centered, below photo) */}
            <div className="flex justify-center mb-6 md:hidden">
              <div className="bg-white p-4 rounded-xl shadow-lg border-2 border-primary-500 flex flex-col items-center">
                <div ref={qrRef}>
                  <QRCode
                    value={`${PRODUCTION_BASE_URL} /candidates/${candidate.link || candidate.id}?is - qr=true`}
                    size={100}
                    level="M"
                    bgColor="#ffffff"
                    fgColor="#000000"
                  />
                </div>
                <div className="mt-2">
                  <span className="text-sm font-bold text-primary-600">Scan to Donate</span>
                </div>
                {/* Copy Link and Save QR buttons */}
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={handleCopyLink}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-full text-gray-700 transition-colors font-medium"
                  >
                    {copied ? (
                      <>
                        <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Copied!
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                        </svg>
                        Copy Link
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleDownloadQR}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs bg-primary-100 hover:bg-primary-200 rounded-full text-primary-700 transition-colors font-medium"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Save QR
                  </button>
                </div>
              </div>
            </div>
            <div className="flex justify-center mb-6 md:hidden">
              <button
                onClick={handleDonateClick}
                className="w-full bg-primary-600 text-white py-4 px-8 rounded-2xl font-bold text-lg hover:bg-primary-700 transition-all duration-300 shadow-xl hover:shadow-2xl flex items-center justify-center gap-2"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                Donate Now
              </button>
            </div>

            {/* Header with Name and QR Code - Desktop */}
            <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{candidate.name}</h1>
                <p className="text-lg text-gray-600 flex items-center gap-2">
                  <svg className="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {candidate.province && `${candidate.province}, `}{candidate.district}, {candidate.constituency}
                </p>
                {candidate.personalWebsite && (
                  <div className="mt-3">
                    <a
                      href={candidate.personalWebsite.startsWith('http') ? candidate.personalWebsite : `https://${candidate.personalWebsite}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-primary-200 rounded-full text-primary-600 hover:bg-primary-50 transition-colors shadow-sm text-sm font-semibold"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                      </svg>
                      Personal Website
                    </a >
                  </div >
                )}
              </div >

              {/* QR Code Section - Desktop (right side) */}
              < div className="hidden md:flex md:flex-col md:items-center bg-white p-4 rounded-xl shadow-lg border-2 border-primary-500 ml-4" >
                <div ref={qrRef}>
                  <QRCode
                    value={`${PRODUCTION_BASE_URL}/candidates/${candidate.link || candidate.id}?is-qr=true`}
                    size={100}
                    level="M"
                    bgColor="#ffffff"
                    fgColor="#000000"
                  />
                </div>
                <div className="mt-2">
                  <span className="text-sm font-bold text-primary-600">Scan to Donate</span>
                </div>
                {/* Copy Link and Save QR buttons */}
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={handleCopyLink}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-full text-gray-700 transition-colors font-medium"
                  >
                    {copied ? (
                      <>
                        <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Copied!
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                        </svg>
                        Copy Link
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleDownloadQR}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs bg-primary-100 hover:bg-primary-200 rounded-full text-primary-700 transition-colors font-medium"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Save QR
                  </button>
                </div>
              </div >
            </div >

            <div className="hidden md:block mb-10">
              <button
                onClick={handleDonateClick}
                className="w-full bg-primary-600 text-white py-4 px-8 rounded-2xl font-bold text-lg hover:bg-primary-700 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 flex items-center justify-center gap-2"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                Donate Now
              </button>
            </div>



            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <span className="w-2 h-8 bg-primary-600 rounded-full"></span>
                About Candidate
              </h2>
              <div className="prose max-w-none text-gray-700 whitespace-pre-wrap leading-relaxed bg-gray-50 p-6 rounded-2xl border border-gray-100">
                {candidate.bio}
              </div>
            </div>

            {/* Detailed Profile Grid */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <span className="w-2 h-8 bg-primary-600 rounded-full"></span>
                Detailed Profile
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <DetailItem label="Educational Qualification" value={candidate.educationalQualification} />
                <DetailItem label="Expertise Area" value={candidate.expertiseArea} />
                <DetailItem label="Current Profession" value={candidate.currentJob || candidate.currentJobOther} />
                <DetailItem label="Experience" value={candidate.yearsOfExperience ? `${candidate.yearsOfExperience} Years` : null} />
                <DetailItem label="Major Contributions" value={candidate.majorContributions} />
                <DetailItem label="Social Contributions" value={candidate.socialContribution} />
                <DetailItem label="Ethnicity" value={candidate.ethnicity} />
              </div>
            </div>

            {/* Politics & History */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <span className="w-2 h-8 bg-primary-600 rounded-full"></span>
                Political Background
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <DetailItem label="Party Involvement" value={candidate.involvedInPoliticalParty ? "Yes" : "No"} />
                <DetailItem label="Party Relation" value={candidate.politicalPartyRelation} />
                <DetailItem label="Political History" value={candidate.politicsBefore} />
                <DetailItem label="Previous Election" value={candidate.previousElection} />
                <DetailItem label="Previous Election Details" value={candidate.detailsOfLastElection} />
                <DetailItem label="Votes Received" value={candidate.votesReceived} />
                <DetailItem label="Revolution Contribution" value={candidate.contributionInRevolution} />
              </div>
            </div>

            {/* Vision & Area Problems */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <span className="w-2 h-8 bg-primary-600 rounded-full"></span>
                Vision & Local Issues
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <DetailItem label="Core Vision" value={candidate.summary} />
                <DetailItem label="Main Local Problems" value={candidate.mainProblemOfArea} />
                <DetailItem label="Priority to Solve" value={candidate.firstProblemToSolve} />
                <DetailItem label="Why Vote for Me?" value={candidate.whyYou} />
              </div>
            </div>

            {/* Associated Issues & Solutions */}
            {
              candidate.issuesAndSolutions && candidate.issuesAndSolutions.length > 0 && (
                <div className="mb-12">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                    <span className="w-2 h-8 bg-primary-600 rounded-full"></span>
                    Local Issues & Solutions
                  </h2>
                  <div className="space-y-4">
                    {candidate.issuesAndSolutions.map((item, idx) => (
                      <div key={idx} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                        <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                          <span className="text-red-500 font-black">Issue:</span> {item.issue}
                        </h4>
                        <p className="text-gray-600 leading-relaxed">
                          <span className="text-green-600 font-bold">Solution:</span> {item.solution}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )
            }

            {/* Campaign Schedule */}
            {
              candidate.campaignSchedule && candidate.campaignSchedule.length > 0 && (
                <div className="mb-12">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                    <span className="w-2 h-8 bg-primary-600 rounded-full"></span>
                    Campaign Schedule
                  </h2>
                  <div className="overflow-hidden rounded-2xl border border-gray-100 shadow-sm">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Date</th>
                          <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Location</th>
                          <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Activity</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {candidate.campaignSchedule.map((item, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{item.date}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.location || item.place}</td>
                            <td className="px-6 py-4 text-sm text-gray-600">{item.activity}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )
            }

            {/* Expenditures List */}
            {
              candidate.expenditures && candidate.expenditures.length > 0 && (
                <div className="mb-12">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                    <span className="w-2 h-8 bg-green-600 rounded-full"></span>
                    Campaign Expenditures
                  </h2>
                  <div className="grid grid-cols-1 gap-6">
                    {candidate.expenditures.map((exp) => (
                      <div key={exp.id} className="bg-white border border-gray-100 rounded-2xl p-6 shadow-md hover:shadow-lg transition-shadow">
                        <div className="flex flex-col md:flex-row justify-between md:items-start gap-4">
                          <div className="flex-grow">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xs font-bold text-gray-400 uppercase">{new Date(exp.createdAt).toLocaleDateString()}</span>
                            </div>
                            <h4 className="text-xl font-bold text-gray-900 mb-2">{exp.title}</h4>
                            {exp.description && <p className="text-gray-600 text-sm mb-4 leading-relaxed">{exp.description}</p>}
                          </div>
                        </div>

                        {(exp.imagesUrl || exp.images) && (exp.imagesUrl || exp.images).length > 0 && (
                          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide py-2">
                            {(exp.imagesUrl || exp.images).map((img, idx) => (
                              <a key={idx} href={img} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                                <img src={img} alt="Expenditure" className="h-32 w-48 object-cover rounded-xl border border-gray-100 hover:opacity-90 transition shadow-sm" />
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )
            }

            {
              candidate.recentDonors && candidate.recentDonors.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Recent Donors</h2>
                  <div className="space-y-3">
                    {candidate.recentDonors.map((donor, index) => (
                      <div key={index} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-semibold text-gray-900">{donor.name}</p>
                          <p className="text-sm text-gray-600">{new Date(donor.date).toLocaleDateString()}</p>
                        </div>
                        <div className="text-lg font-bold text-primary-600">
                          {formatCurrency(donor.amount, lang)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            }

            <button
              onClick={handleDonateClick}
              className="w-full bg-primary-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-primary-700 transition-colors duration-200 shadow-md hover:shadow-lg"
            >
              Donate Now
            </button>
          </div >
        </div >
      </div >

      <PartyDonationModal
        isOpen={showDonateModal}
        onClose={() => setShowDonateModal(false)}
        initialTargetType="CANDIDATE"
        initialCandidateId={candidate?.id}
      />
    </div >
  );
};

export default CandidateProfile;
